/**
 * Custom Hook: useUsersInfinite
 * Handles infinite scrolling pagination for users (Invite Users page) using React Query
 *
 * Features:
 * - Cursor-based pagination
 * - Manual "Load more" button
 * - Backend-driven search (search param passed to API)
 * - Proper cache management
 * - Error handling
 */

import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getUsersByOrganizationPaginated } from '../services/userService';

/** Query key prefix for users list. Use for refetch after create/update/delete. */
export const USERS_QUERY_KEY = ['users'];

/**
 * @param {Object} params
 * @param {string} params.organizationId
 * @param {number} [params.limit=10]
 * @param {string} [params.search]
 */
export const useUsersInfinite = (params = {}) => {
  const { organizationId, enabled = true, limit = 10, search = '' } = params;
  const searchTerm = typeof search === 'string' ? search.trim() : '';

  const query = useInfiniteQuery({
    queryKey: [...USERS_QUERY_KEY, organizationId, limit, searchTerm],
    queryFn: ({ pageParam, signal }) => {
      return getUsersByOrganizationPaginated(organizationId, {
        limit,
        cursor: pageParam,
        search: searchTerm,
        signal,
      });
    },
    getNextPageParam: (lastPage) => {
      const { nextCursor } = lastPage?.pagination ?? {};
      return nextCursor ?? undefined;
    },
    initialPageParam: undefined,
    enabled: !!organizationId && enabled,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const users = useMemo(
    () => query.data?.pages?.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const totalCount = query.data?.pages?.[query.data.pages.length - 1]?.pagination?.totalCount ?? 0;
  const hasNextPage = query.hasNextPage ?? false;

  return {
    users,
    totalCount,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
  };
};
